import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDebounce } from "../../../../core/common/useDebounce";
import API_URLS from "../../../../core/common/apiUrls";

interface HotelLocationDropdownProps {
  label: string;
  defaultCity: string;
  defaultCountry: string;
  onChange?: (selectedLocation: {
    cityName: string;
    countryName: string;
    cityCode: string;
    countryCode: string;
  }) => void;
}

interface HotelLocationResult {
  CityCode: string;
  CityName: string;
  CountryName: string;
  CountryCode: string;
}

export const HotelLocationDropdown: React.FC<HotelLocationDropdownProps> = ({
  label,
  defaultCity,
  defaultCountry,
  onChange,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<HotelLocationResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    if (debouncedSearchTerm.trim() === "") {
      setResults([]);
      setOpen(false);
      return;
    }

    setLoading(true);
    fetch(`${API_URLS.HOTELS.SEARCH_CITIES}?query=${encodeURIComponent(debouncedSearchTerm)}`)
      .then((res) => res.json())
      .then((data) => {
        setResults(data?.results || []);
        setOpen(true);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Hotel city fetch failed:", err);
        setLoading(false);
      });
  }, [debouncedSearchTerm]);

  const handleSelectLocation = (loc: HotelLocationResult) => {
    if (onChange) {
      onChange({
        cityName: loc.CityName,
        countryName: loc.CountryName,
        cityCode: loc.CityCode,
        countryCode: loc.CountryCode,
      });
    }
    setSearchTerm(loc.CityName);
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="form-item dropdown">
      <label className="form-label fs-14 text-default mb-1">{label}</label>
      <input
        type="text"
        className="form-control"
        value={searchTerm}
        placeholder={defaultCity}
        onChange={(e) => setSearchTerm(e.target.value)}
        onFocus={() => results.length > 0 && setOpen(true)}
      />
      <p className="fs-12 mb-0">{defaultCountry}</p>

      <div className={`dropdown-menu dropdown-md p-0 ${open ? "show" : ""}`}>
        <ul>
          {loading && (
            <li className="px-3 py-2">
              <em>Loading...</em>
            </li>
          )}
          {!loading && results.length === 0 && searchTerm && (
            <li className="px-3 py-2">
              <em>No results found</em>
            </li>
          )}
          {results.map((loc, index) => (
            <li
              key={index}
              className="border-bottom"
              onClick={() => handleSelectLocation(loc)}
            >
              <Link className="dropdown-item" to="#">
                <h6 className="fs-16 fw-medium">{loc.CityName}</h6>
                <p>{loc.CountryName}</p>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
